/**
 * Ventana «quedó registrado»: lo que se puede imprimir se ofrece en una ventana que se queda en pantalla hasta que el
 * usuario la cierra, igual que la de la venta emitida. Pedido de la clienta (03-10-2026): el aviso pasajero con el
 * botón «Imprimir» desaparecía antes de alcanzarlo. Regla: ningún aviso pasajero (toast) lleva botón de imprimir.
 *
 * Se llama con `mostrarRegistrado(...)` desde cualquier pantalla. La ventana vive una sola vez, en el `AppLayout`,
 * así sobrevive al cierre del formulario que la originó. Si llegan dos seguidas, salen una tras otra.
 */
import { Printer } from 'lucide-react';
import { useSyncExternalStore } from 'react';
import { useNavigate } from 'react-router';
import { Dinero, type TasasDocumento } from '@/componentes/Dinero.tsx';
import { Button } from '@/componentes/ui/button.tsx';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/componentes/ui/dialog.tsx';

export interface Registrado {
  titulo: string;
  descripcion: string;
  /**
   * Renglones debajo del título. Los de `aviso` salen resaltados. Con `monto`, el renglón lleva a la derecha el monto en
   * Bs, $ a BCV y $ divisa, como el POS (pedido de la dueña 19-10-2026): `doc` son las tasas del documento; `referencial`,
   * las de hoy (se marca «ref.»).
   */
  detalle?: { texto: string; aviso?: boolean; monto?: { valor: string; bs?: string | undefined; doc?: TasasDocumento | undefined; referencial?: TasasDocumento | undefined; testid?: string } }[];
  /** Hojas que se pueden imprimir: cada botón abre la suya en otra pestaña. */
  imprimir: { etiqueta: string; ruta: string }[];
  /** Otro camino desde la ventana (p. ej. «Ver ajustes»). */
  ir?: { etiqueta: string; ruta: string };
  /** Otras acciones que no cierran la ventana (p. ej. «Copiar mensaje para WhatsApp»). */
  acciones?: { etiqueta: string; alHacer: () => void; testid?: string }[];
  /** Al cerrar la ventana (p. ej. devolver el foco al buscador o abrir el detalle). */
  alCerrar?: () => void;
}

let cola: Registrado[] = [];
const oyentes = new Set<() => void>();
const avisar = () => { for (const o of oyentes) o(); };
const suscribir = (o: () => void) => { oyentes.add(o); return () => { oyentes.delete(o); }; };
const primero = () => cola[0] ?? null;

export function mostrarRegistrado(r: Registrado) {
  cola = [...cola, r];
  avisar();
}

export function VentanaRegistrado() {
  const r = useSyncExternalStore(suscribir, primero, () => null);
  const navigate = useNavigate();
  const cerrar = () => {
    const cerrado = cola[0];
    cola = cola.slice(1);
    avisar();
    cerrado?.alCerrar?.();
  };
  return (
    <Dialog open={!!r} onOpenChange={(o) => { if (!o) cerrar(); }}>
      {/* No se cierra con un clic fuera: solo con «Listo», la X o Escape */}
      <DialogContent data-testid="dialogo-registrado" onInteractOutside={(e) => e.preventDefault()}>
        {r ? (
          <>
            <DialogHeader>
              <DialogTitle data-testid="registrado-titulo">{r.titulo}</DialogTitle>
              <DialogDescription data-testid="registrado-descripcion">{r.descripcion}</DialogDescription>
            </DialogHeader>
            {r.detalle?.length ? (
              <div className="space-y-1 text-sm" data-testid="registrado-detalle">
                {r.detalle.map((d) =>
                  d.monto ? (
                    <p key={d.texto} className="flex items-start justify-between gap-3 border-b border-dashed pb-1 last:border-b-0" data-testid={d.monto.testid}>
                      <span>{d.texto}</span>
                      <Dinero valor={d.monto.valor} modo={d.monto.doc || d.monto.referencial ? 'triple' : 'usd'} doc={d.monto.doc} referencial={d.monto.referencial} bs={d.monto.bs} className="shrink-0" />
                    </p>
                  ) : (
                    <p key={d.texto} className={d.aviso ? 'rounded-lg border border-mg-ambar bg-amber-50 px-3 py-1.5 text-xs font-medium text-amber-900' : undefined}>{d.texto}</p>
                  ),
                )}
              </div>
            ) : null}
            <DialogFooter className="flex-wrap gap-2 sm:space-x-0">
              {r.imprimir.map((h, i) => (
                <Button key={h.ruta} variant="outline" onClick={() => window.open(h.ruta, '_blank')} data-testid={i === 0 ? 'registrado-imprimir' : `registrado-imprimir-${i + 1}`}>
                  <Printer /> {h.etiqueta}
                </Button>
              ))}
              {r.acciones?.map((a) => (
                <Button key={a.etiqueta} variant="outline" onClick={a.alHacer} data-testid={a.testid ?? 'registrado-accion'}>{a.etiqueta}</Button>
              ))}
              {r.ir ? <Button variant="outline" onClick={() => { const { ruta } = r.ir!; cerrar(); navigate(ruta); }} data-testid="registrado-ir">{r.ir.etiqueta}</Button> : null}
              <Button onClick={cerrar} data-testid="registrado-cerrar">Listo</Button>
            </DialogFooter>
          </>
        ) : null}
      </DialogContent>
    </Dialog>
  );
}
